/**
 * Face detection using MediaPipe Tasks Vision (loaded via CDN).
 * Runs continuously on the webcam feed; reports "no_face" if no face for >10s,
 * and "multi_face" if more than one face detected.
 */
import { useEffect, useRef, useState } from "react";

const MEDIAPIPE_URL = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.9/vision_bundle.mjs";
const MODEL_URL = "https://storage.googleapis.com/mediapipe-models/face_detector/blaze_face_short_range/float16/1/blaze_face_short_range.tflite";

export function useFaceDetector({ enabled, videoRef, onNoFace, onMultiFace, checkIntervalMs = 1000, noFaceThresholdMs = 10000 }) {
  const [status, setStatus] = useState("idle"); // idle | loading | running | error | denied
  const detectorRef = useRef(null);
  const rafRef = useRef(null);
  const lastFaceAtRef = useRef(Date.now());
  const noFaceReportedRef = useRef(false);
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    async function setup() {
      try {
        setStatus("loading");
        const mp = await import(/* webpackIgnore: true */ MEDIAPIPE_URL);
        const { FaceDetector, FilesetResolver } = mp;
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.9/wasm"
        );
        const detector = await FaceDetector.createFromOptions(vision, {
          baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
          runningMode: "VIDEO"
        });
        if (cancelled) { detector.close?.(); return; }
        detectorRef.current = detector;
        setStatus("running");
        // Loop
        let lastRun = 0;
        const loop = () => {
          if (cancelled || !enabledRef.current) return;
          const video = videoRef.current;
          const now = performance.now();
          if (video && video.readyState >= 2 && now - lastRun > checkIntervalMs) {
            lastRun = now;
            try {
              const res = detector.detectForVideo(video, Date.now());
              const count = res?.detections?.length || 0;
              if (count === 0) {
                if (Date.now() - lastFaceAtRef.current > noFaceThresholdMs && !noFaceReportedRef.current) {
                  noFaceReportedRef.current = true;
                  onNoFace?.();
                }
              } else {
                lastFaceAtRef.current = Date.now();
                noFaceReportedRef.current = false;
                if (count > 1) onMultiFace?.(count);
              }
            } catch (e) { /* transient error */ }
          }
          rafRef.current = requestAnimationFrame(loop);
        };
        rafRef.current = requestAnimationFrame(loop);
      } catch (e) {
        console.error("Face detector setup error", e);
        setStatus("error");
      }
    }
    setup();
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      detectorRef.current?.close?.();
      detectorRef.current = null;
    };
  }, [enabled, videoRef, onNoFace, onMultiFace, checkIntervalMs, noFaceThresholdMs]);

  return { status };
}


/**
 * Mic spike detection using Web Audio API. Fires onSpike when average
 * volume crosses `threshold` for a sustained duration.
 */
export function useMicSpike({ enabled, stream, onSpike, threshold = 45, sustainedMs = 1200 }) {
  const [status, setStatus] = useState("idle");
  const rafRef = useRef(null);
  const overStartRef = useRef(null);
  const enabledRef = useRef(enabled);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  useEffect(() => {
    if (!enabled || !stream) return;
    let cancelled = false;
    let audioCtx = null;
    let analyser = null;
    let source = null;
    try {
      audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      analyser = audioCtx.createAnalyser();
      analyser.fftSize = 512;
      source = audioCtx.createMediaStreamSource(stream);
      source.connect(analyser);
      const buf = new Uint8Array(analyser.frequencyBinCount);
      setStatus("running");
      const loop = () => {
        if (cancelled || !enabledRef.current) return;
        analyser.getByteFrequencyData(buf);
        let sum = 0;
        for (let i = 0; i < buf.length; i++) sum += buf[i];
        const avg = sum / buf.length;
        if (avg > threshold) {
          if (!overStartRef.current) overStartRef.current = Date.now();
          if (Date.now() - overStartRef.current > sustainedMs) {
            overStartRef.current = null;
            onSpike?.(avg);
          }
        } else {
          overStartRef.current = null;
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      console.error("Mic setup error", e);
      setStatus("error");
    }
    return () => {
      cancelled = true;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      try { source?.disconnect(); } catch (e) { /* noop */ }
      try { audioCtx?.close(); } catch (e) { /* noop */ }
    };
  }, [enabled, stream, onSpike, threshold, sustainedMs]);

  return { status };
}
